import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EndpointRow {
  readonly endpointId: string;
  readonly endpointName: string;
  readonly integrationType: string;
  readonly provider: string;
  readonly status: string;
}

export function M20IntegrationDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EndpointRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const [endpointName, setEndpointName] = useState('');
  const [integrationType, setIntegrationType] = useState('ERP');
  const [provider, setProvider] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: EndpointRow[] }>('/v1/integration/endpoints', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/integration/endpoints', {
        method: 'POST', token,
        body: { endpointName, integrationType, provider: provider || undefined, baseUrl: baseUrl || undefined },
      });
      setCreated(true);
      setEndpointName('');
      setProvider('');
      setBaseUrl('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const testConnection = async (id: string): Promise<void> => {
    setBusyId(id + 'test');
    setTestResult(null);
    try {
      await apiRequest(`/v1/integration/endpoints/${id}/test`, { method: 'POST', token });
      setTestResult(t('m20.dashboard.testTriggered', { id }));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (id: string, status: string): Promise<void> => {
    setBusyId(id + status);
    try {
      await apiRequest(`/v1/integration/endpoints/${id}`, { method: 'PATCH', token, body: { status } });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m20.dashboard.loading')}</p>;
  return (
    <main aria-label="integration-dashboard">
      <h1>{t('m20.dashboard.title')}</h1>

      <form onSubmit={onCreate} aria-label="endpoint-create-form">
        <h2>{t('m20.dashboard.newEndpoint')}</h2>
        <label htmlFor="ep-name">{t('m20.dashboard.name')}</label>
        <input id="ep-name" value={endpointName} onChange={(e) => setEndpointName(e.target.value)} required />
        <label htmlFor="ep-type">{t('m20.dashboard.integrationType')}</label>
        <select id="ep-type" aria-label="integration-type" value={integrationType} onChange={(e) => setIntegrationType(e.target.value)}>
          <option value="ERP">{t('m20.dashboard.typeErp')}</option>
          <option value="CRM">{t('m20.dashboard.typeCrm')}</option>
          <option value="BANKING">{t('m20.dashboard.typeBanking')}</option>
          <option value="GIS">{t('m20.dashboard.typeGis')}</option>
          <option value="SSO">{t('m20.dashboard.typeSso')}</option>
          <option value="EMAIL">{t('m20.dashboard.typeEmail')}</option>
          <option value="SMS">{t('m20.dashboard.typeSms')}</option>
          <option value="WHATSAPP">{t('m20.dashboard.typeWhatsapp')}</option>
          <option value="FILE">{t('m20.dashboard.typeFile')}</option>
        </select>
        <label htmlFor="ep-provider">{t('m20.dashboard.provider')}</label>
        <input id="ep-provider" value={provider} onChange={(e) => setProvider(e.target.value)} />
        <label htmlFor="ep-url">{t('m20.dashboard.baseUrl')}</label>
        <input id="ep-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m20.dashboard.createdMsg')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m20.dashboard.creating') : t('m20.dashboard.createBtn')}</button>
      </form>

      {testResult ? <p role="status">{testResult}</p> : null}

      <table aria-label="integration-endpoints-table">
        <thead><tr><th>{t('m20.dashboard.colName')}</th><th>{t('m20.dashboard.colType')}</th><th>{t('m20.dashboard.colProvider')}</th><th>{t('m20.dashboard.colStatus')}</th><th>{t('m20.dashboard.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.endpointId}>
              <td>{e.endpointName}</td>
              <td>{e.integrationType}</td>
              <td>{e.provider}</td>
              <td>{e.status}</td>
              <td>
                <button type="button" aria-label={`test-${e.endpointId}`} disabled={busyId === e.endpointId + 'test'} onClick={() => void testConnection(e.endpointId)}>{t('m20.dashboard.test')}</button>
                {e.status === 'ACTIVE' ? (
                  <button type="button" aria-label={`disable-${e.endpointId}`} disabled={busyId === e.endpointId + 'DISABLED'} onClick={() => void setStatus(e.endpointId, 'DISABLED')}>{t('m20.dashboard.disable')}</button>
                ) : (
                  <button type="button" aria-label={`enable-${e.endpointId}`} disabled={busyId === e.endpointId + 'ACTIVE'} onClick={() => void setStatus(e.endpointId, 'ACTIVE')}>{t('m20.dashboard.enable')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
